// pendingActions.js
// A held write with a window to undo it. Pure, no React, no network: the
// caller supplies `run` (the write, called with { keepalive } on a flush from
// unload) and `restore` (put the optimistic local state back). Keys are the
// item id for a single action or any string for a batch, which also carries
// `ids` so a later single action on one of its items flushes the batch first.
//
//   schedule(key, { run, restore, ids? })  hold `run` for delayMs; an earlier
//                                          pending entry on the same key, or a
//                                          batch containing this key, is FLUSHED
//                                          (its write fires now), never dropped
//   undo(key)                              cancel the timer, restore, no write
//   flush(key) / flushAll(opts)            fire now (unload, or a second action)
//
// A write never runs twice: fire() deletes the entry before calling run.

export const UNDO_MS = 5000;

export function createPending({ delayMs = UNDO_MS } = {}) {
  const map = new Map();

  const fire = (key, opts) => {
    const p = map.get(key);
    if (!p) return null;
    clearTimeout(p.timer);
    map.delete(key);
    try { return p.run(opts || {}); } catch (e) { return Promise.reject(e); }
  };
  const idsOf = (key, p) => (Array.isArray(p.ids) ? p.ids : [key]);

  return {
    size: () => map.size,
    has: (key) => map.has(key),
    keys: () => [...map.keys()],
    schedule(key, { run, restore, ids = null }) {
      if (typeof run !== 'function' || typeof restore !== 'function') throw new Error('pending: run and restore are required');
      const mine = new Set(ids && ids.length ? ids : [key]);
      // Flush anything already pending on these ids, batch or single.
      for (const [k, p] of [...map.entries()]) {
        if (k === key || idsOf(k, p).some((id) => mine.has(id))) fire(k);
      }
      const timer = setTimeout(() => fire(key), delayMs);
      map.set(key, { timer, run, restore, ids: ids && ids.length ? [...ids] : null });
      return key;
    },
    undo(key) {
      const p = map.get(key);
      if (!p) return false;
      clearTimeout(p.timer);
      map.delete(key);
      p.restore();
      return true;
    },
    flush(key, opts) { return fire(key, opts); },
    flushAll(opts) { return map.size ? [...map.keys()].map((k) => fire(k, opts)) : []; },
  };
}
